import ExitToAppOutlinedIcon from "@mui/icons-material/ExitToAppOutlined";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import FileUploadOutlinedIcon from "@mui/icons-material/FileUploadOutlined";
import { Container, Stack } from "@mui/material";

import React, { useEffect, useState } from "react";

import { IntegrationState } from "../ServerEndpoints";
import { DropSignedFile, Header, SignedFlowErrorAlert, SignedFlowTimerAlert, Step } from "../components";
import { createValidatedFileUpload } from "../helpers/validatedFileUpload";
import { getSignedFlowExpiration, useSignedFlowTimer } from "../hooks/useSignedFlowTimer";
import { useStore } from "../store";

/**
 * Profil zaufany: pobierz plik → podpisz w moj.gov.pl → wgraj plik.
 * Wspólna logika z SignatureMethod: kroki sekwencyjne, timer 10 min od pobrania,
 * przy timeout lub błędzie wgrywania przycisk "Od nowa" restartuje bieżącą metodę (bez wychodzenia z widoku).
 */
export const TrustedProfileMethod = () => {
  const { integration, getIntegrationsData, uploadSignedFile, createFileToSign, restartCurrentMethod } = useStore();
  const integrationStatus = integration?.state;
  const [steps, setSteps] = useState<{ [key in number]: boolean }>({});
  const setStep = (step: number) => setSteps((s) => ({ ...s, [step]: true }));
  const [preparedFileToDownload, setPreparedFileToDownload] = useState(false);
  const [uploadError, setUploadError] = useState(false);
  const [didDownloadInThisSession, setDidDownloadInThisSession] = useState(false);
  const { timerExpired, startTimer, clearTimer, remainingFormatted } = useSignedFlowTimer();

  const step1Done = integrationStatus === IntegrationState.SessionSignedRequestGenerated;
  const step2Done = steps[2];
  const step3Unlocked = step2Done;
  const blockFlow = timerExpired;

  useEffect(() => {
    if (integration?.state !== IntegrationState.SessionSignedRequestGenerated) return;
    startTimer(getSignedFlowExpiration(integration));
  }, [integration?.state, integration?.dateLastStateUpdate]); // eslint-disable-line react-hooks/exhaustive-deps -- startTimer is stable; only re-run when integration timer source changes

  const onDownloadFile = async () => {
    setPreparedFileToDownload(true);
    await createFileToSign();
    setStep(1);
    setDidDownloadInThisSession(true);
    setPreparedFileToDownload(false);
    const refreshed = await getIntegrationsData();
    if (!refreshed) startTimer();
  };

  const onClickPZ = () => {
    window.open("https://moj.gov.pl/nforms/signer/upload?xFormsAppName=SIGNER&xadesPdf=true", "_blank");
    setStep(2);
  };

  const onUploadFile = createValidatedFileUpload(["xml"], async (file) => {
    setUploadError(false);
    const success = await uploadSignedFile(file);
    if (success) clearTimer();
    else setUploadError(true);
  });

  const onRestart = async () => {
    clearTimer();
    setUploadError(false);
    setSteps({});
    setDidDownloadInThisSession(false);
    await restartCurrentMethod();
  };

  return (
    <Container maxWidth="sm">
      <Stack spacing={4}>
        <Header titleLoc="trustedProfileMethod.title" onBackPath="/" />

        <Stack spacing={2}>
          <SignedFlowTimerAlert
            remainingFormatted={remainingFormatted}
            timerExpired={timerExpired}
            onRestart={onRestart}
          />
          <SignedFlowErrorAlert uploadError={uploadError} onRestart={onRestart} />
        </Stack>

        <Stack spacing={1}>
          <Step
            step={1}
            titleLoc="common.downloadFileToSign"
            description="trustedProfileMethod.step1Description"
            buttonLabelLoc="common.download"
            done={step1Done}
            disabled={blockFlow}
            icon={<FileDownloadOutlinedIcon color="primary" sx={{ fontSize: 36 }} />}
            onClick={onDownloadFile}
            loading={preparedFileToDownload}
          />

          <Step
            step={2}
            titleLoc="trustedProfileMethod.goToYourTrustedProfileAndSign"
            description="trustedProfileMethod.step2Description"
            replayLabelLoc="common.goAgain"
            buttonLabelLoc="common.moveOn"
            {...(!didDownloadInThisSession &&
              step1Done &&
              !blockFlow && {
                secondaryButtonLabelLoc: "common.skip",
                secondaryButtonTooltipLoc: "trustedProfileMethod.iAlreadyHaveSignedFile",
                onSecondaryClick: () => setStep(2),
              })}
            done={step2Done}
            disabled={!step1Done || blockFlow}
            icon={<ExitToAppOutlinedIcon color="primary" sx={{ fontSize: 36 }} />}
            onClick={onClickPZ}
          />

          <Step
            step={3}
            titleLoc="common.addSignedFile"
            description="trustedProfileMethod.step3Description"
            buttonLabelLoc="common.add"
            done={steps[3]}
            showDone={false}
            disabled={!step3Unlocked || blockFlow}
            icon={<FileUploadOutlinedIcon color="primary" sx={{ fontSize: 36 }} />}
            onClick={() => setStep(3)}
          />

          {steps[3] && !blockFlow && (
            <DropSignedFile acceptedFormats=".xml" minHeight={8 * 16} onFileUpload={onUploadFile} />
          )}
        </Stack>
      </Stack>
    </Container>
  );
};
